import { useEffect, useRef } from "react";
import { fieldNameOf, ValidationErrorType } from "./events";
import { trackValidationError } from "./index";

export { fieldNameOf };

/**
 * Firma de un valor de error tal como lo guardan las pantallas: un texto, un
 * booleano o un objeto `{ error, message }`. null = no hay error.
 */
export const errorSignature = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value.trim() ? value : null;
  if (typeof value === "boolean") return value ? "error" : null;
  if (typeof value === "object") {
    const v = value as { error?: unknown; message?: unknown };
    if ("error" in v) return v.error ? String(v.message || "error") : null;
    if ("message" in v) return v.message ? String(v.message) : null;
  }
  return null;
};

/**
 * Devuelve una función que, cada vez que se le pasa el objeto de errores de una
 * pantalla, emite `field_invalid` por cada campo que pasa a tener error (o
 * cambia de error). Nunca manda el valor del campo ni el texto del error: solo
 * el nombre del campo.
 */
export const createValidationErrorTracker = (
  errorType: ValidationErrorType = "format"
): ((errors: Record<string, unknown> | null | undefined) => void) => {
  let previous: Record<string, string> = {};
  return (errors) => {
    const current: Record<string, string> = {};
    Object.keys(errors || {}).forEach((key) => {
      const signature = errorSignature((errors as Record<string, unknown>)[key]);
      if (!signature) return;
      current[key] = signature;
      if (previous[key] !== signature) trackValidationError(fieldNameOf(key), errorType);
    });
    previous = current;
  };
};

/** Lo mismo que `createValidationErrorTracker`, como hook sobre el estado de errores. */
export const useTrackValidationErrors = (
  errors: Record<string, unknown> | null | undefined,
  errorType: ValidationErrorType = "format"
): void => {
  const trackErrors = useRef(createValidationErrorTracker(errorType)).current;
  const fingerprint = JSON.stringify(
    Object.keys(errors || {}).map((key) => [
      key,
      errorSignature((errors as Record<string, unknown>)[key]),
    ])
  );
  useEffect(() => {
    trackErrors(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);
};
